import React, { Component } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  FormControl,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCopy, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { compareIndexAnalysisInfo } from "../../services/marketDataService";
import "./MarketDataComparison.css";

class IndexAnalysisComparison extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Source 1 inputs
      source1Host: "192.168.29.113",
      source1Symbol: "NIFTY 50",
      source1Date: "2025-12-02",
      source1Time: "09:17:00",
      
      // Source 2 inputs
      source2Host: "192.168.29.113",
      source2Symbol: "NIFTY 50",
      source2Date: "2025-12-02",
      source2Time: "09:17:00",
      
      // Results
      comparisonResult: null,
      error: null,
      isLoading: false,
      
      // Available symbols
      symbols: [
        { value: "NIFTY 50", label: "NIFTY 50" },
        { value: "NIFTY BANK", label: "NIFTY BANK" },
        { value: "NIFTY FIN SERVICE", label: "FINNIFTY" },
        { value: "SENSEX", label: "SENSEX" },
        { value: "BANKEX", label: "BANKEX" },
        { value: "NIFTY MID SELECT", label: "MIDCPNIFTY" },
      ],
    };
  }

  handleInputChange = (field, value) => {
    this.setState({
      [field]: value,
      error: null,
      comparisonResult: null,
    });
  };

  // Convert date and time to epoch timestamp in milliseconds
  dateTimeToTimestamp = (date, time) => {
    const dateTimeStr = `${date}T${time}`;
    const dateTime = new Date(dateTimeStr);
    return dateTime.getTime();
  };

  handleCompare = async () => {
    const {
      source1Host,
      source1Symbol,
      source1Date,
      source1Time,
      source2Host,
      source2Symbol,
      source2Date,
      source2Time,
    } = this.state;

    // Validation
    if (!source1Host || !source1Symbol || !source1Date || !source1Time) {
      this.setState({ error: "Please fill all Source 1 fields" });
      return;
    }

    if (!source2Host || !source2Symbol || !source2Date || !source2Time) {
      this.setState({ error: "Please fill all Source 2 fields" });
      return;
    }

    this.setState({ isLoading: true, error: null, comparisonResult: null });

    try {
      // Convert date/time to timestamps
      const source1Timestamp = this.dateTimeToTimestamp(source1Date, source1Time);
      const source2Timestamp = this.dateTimeToTimestamp(source2Date, source2Time);

      const result = await compareIndexAnalysisInfo({
        source1Host,
        source1Symbol,
        source1Timestamp,
        source2Host,
        source2Symbol,
        source2Timestamp,
      });

      this.setState({
        comparisonResult: result,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to compare records. Please check your inputs and try again.";
      this.setState({
        error: errorMessage,
        isLoading: false,
        comparisonResult: null,
      });
    }
  };

  copyToClipboard = () => {
    if (this.state.comparisonResult) {
      const jsonString = JSON.stringify(this.state.comparisonResult, null, 2);
      navigator.clipboard.writeText(jsonString).then(() => {
        alert("JSON copied to clipboard!");
      });
    }
  };

  render() {
    const {
      source1Host,
      source1Symbol,
      source1Date,
      source1Time,
      source2Host,
      source2Symbol,
      source2Date,
      source2Time,
      comparisonResult,
      error,
      isLoading,
      symbols,
    } = this.state;

    return (
      <div className="market-data-comparison">
        <Card>
          <Card.Header>
            <h4>IndexAnalysisInfo Comparison</h4>
            <p className="text-muted mb-0">
              Compare IndexAnalysisInfo records from two different MongoDB sources
            </p>
          </Card.Header>
          <Card.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => this.setState({ error: null })}>
                {error}
              </Alert>
            )}

            <Row>
              {/* Source 1 */}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-primary text-white">
                    <strong>Source S1</strong>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>MongoDB Host</Form.Label>
                      <FormControl
                        type="text"
                        value={source1Host}
                        onChange={(e) =>
                          this.handleInputChange("source1Host", e.target.value)
                        }
                        placeholder="e.g., 192.168.29.113"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Symbol</Form.Label>
                      <FormControl
                        as="select"
                        value={source1Symbol}
                        onChange={(e) =>
                          this.handleInputChange("source1Symbol", e.target.value)
                        }
                      >
                        {symbols.map((sym) => (
                          <option key={sym.value} value={sym.value}>
                            {sym.label}
                          </option>
                        ))}
                      </FormControl>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <FormControl
                        type="date"
                        value={source1Date}
                        onChange={(e) =>
                          this.handleInputChange("source1Date", e.target.value)
                        }
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Time</Form.Label>
                      <FormControl
                        type="time"
                        value={source1Time}
                        onChange={(e) =>
                          this.handleInputChange("source1Time", e.target.value)
                        }
                        step="1"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Source 2 */}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-success text-white">
                    <strong>Source S2</strong>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>MongoDB Host</Form.Label>
                      <FormControl
                        type="text"
                        value={source2Host}
                        onChange={(e) =>
                          this.handleInputChange("source2Host", e.target.value)
                        }
                        placeholder="e.g., 192.168.29.113"
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Symbol</Form.Label>
                      <FormControl
                        as="select"
                        value={source2Symbol}
                        onChange={(e) =>
                          this.handleInputChange("source2Symbol", e.target.value)
                        }
                      >
                        {symbols.map((sym) => (
                          <option key={sym.value} value={sym.value}>
                            {sym.label}
                          </option>
                        ))}
                      </FormControl>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <FormControl
                        type="date"
                        value={source2Date}
                        onChange={(e) =>
                          this.handleInputChange("source2Date", e.target.value)
                        }
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Time</Form.Label>
                      <FormControl
                        type="time"
                        value={source2Time}
                        onChange={(e) =>
                          this.handleInputChange("source2Time", e.target.value)
                        }
                        step="1"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Compare Button */}
            <Row>
              <Col md={12} className="text-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={this.handleCompare}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Comparing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="mr-2" />
                      Compare
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            {/* Results */}
            {comparisonResult && (
              <Row className="mt-4">
                <Col md={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>Comparison Results - Differences Only</strong>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={this.copyToClipboard}
                      >
                        <FontAwesomeIcon icon={faCopy} className="mr-1" />
                        Copy JSON
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {/* Statistics */}
                      <div className="mb-3">
                        <Row>
                          <Col md={4}>
                            <strong>Source 1:</strong>{" "}
                            {comparisonResult.source1Info || "N/A"}
                          </Col>
                          <Col md={4}>
                            <strong>Source 2:</strong>{" "}
                            {comparisonResult.source2Info || "N/A"}
                          </Col>
                          <Col md={4}>
                            <strong>Different Fields:</strong>{" "}
                            {comparisonResult.differentFieldsCount || 0}
                          </Col>
                        </Row>
                      </div>

                      {/* JSON View */}
                      {comparisonResult.differences &&
                      Object.keys(comparisonResult.differences).length > 0 ? (
                        <div className="json-viewer">
                          <pre className="json-content">
                            {JSON.stringify(
                              {
                                source1Info: comparisonResult.source1Info,
                                source2Info: comparisonResult.source2Info,
                                totalFieldsCompared:
                                  comparisonResult.totalFieldsCompared,
                                differentFieldsCount:
                                  comparisonResult.differentFieldsCount,
                                differences: comparisonResult.differences,
                              },
                              null,
                              2
                            )}
                          </pre>
                        </div>
                      ) : (
                        <Alert variant="info">
                          No differences found! The records are identical.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default IndexAnalysisComparison;

